import api from "../services/api";

const ANON_KEY = "examnovaAnonymousId";
const ATTRIBUTION_KEY = "examnovaCampaignAttribution";
const ATTRIBUTION_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "fbclid",
];

const getAnonymousId = () => {
  try {
    const existing = localStorage.getItem(ANON_KEY);
    if (existing) return existing;
    const next = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(ANON_KEY, next);
    return next;
  } catch {
    return "";
  }
};

const queryAttribution = () => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const out = {};
  ATTRIBUTION_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) out[key] = value;
  });
  return out;
};

const readStoredAttribution = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(ATTRIBUTION_KEY) || "null");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

// Capture first-touch campaign context once so navigation and the Auth0 round
// trip do not sever the anonymous visit from signup and learning events.
export const captureAttribution = () => {
  if (typeof window === "undefined") return {};
  const stored = readStoredAttribution();
  if (Object.keys(stored).length) return stored;

  const campaign = queryAttribution();
  const attribution = {
    ...campaign,
    ...(campaign.utm_source ? { source: campaign.utm_source } : {}),
    ...(campaign.utm_medium ? { medium: campaign.utm_medium } : {}),
    ...(campaign.utm_campaign ? { campaign: campaign.utm_campaign } : {}),
    ...(campaign.utm_content ? { creative: campaign.utm_content } : {}),
    landing_page: window.location.pathname,
    landing_search: window.location.search,
    referrer: document.referrer || "",
  };
  try {
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
  } catch {
    // Storage can be unavailable in hardened/private browser contexts.
  }
  return attribution;
};

const deviceCategory = () => {
  if (typeof window === "undefined") return "unknown";
  if (window.innerWidth < 640) return "mobile";
  if (window.innerWidth < 1024) return "tablet";
  return "desktop";
};

export const trackFunnelEvent = (event, metadata = {}) => {
  if (!event || typeof window === "undefined") return Promise.resolve(false);

  const payload = {
    event,
    anonymousId: getAnonymousId(),
    userId: localStorage.getItem("userId") || undefined,
    metadata: {
      path: window.location.pathname,
      search: window.location.search,
      referrer: document.referrer || "",
      device: deviceCategory(),
      ...captureAttribution(),
      ...metadata,
    },
  };

  const endpoint = payload.userId ? "/api/v1/analytics/event" : "/api/v1/public/analytics/event";
  return api.post(endpoint, payload).then(() => true, () => false);
};

// Coalesce pending calls and remember successful delivery per (event, key).
// Failed delivery remains retryable; lifecycle counts are committed by the
// server and do not depend on this browser guard.
const pendingEvents = new Map();
export const trackFunnelEventOnce = (event, metadata = {}, key = "") => {
  if (!event || typeof window === "undefined") return Promise.resolve(false);
  const dedupeKey = `examnova_evt:${event}:${key}`;
  try {
    if (sessionStorage.getItem(dedupeKey)) return Promise.resolve(true);
  } catch {
    // sessionStorage blocked (private mode) — fall through and fire; a rare
    // duplicate is better than dropping the event entirely.
  }
  if (pendingEvents.has(dedupeKey)) return pendingEvents.get(dedupeKey);
  const delivery = trackFunnelEvent(event, metadata).then(delivered => {
    if (delivered) {
      try { sessionStorage.setItem(dedupeKey, "1"); } catch { /* Storage unavailable. */ }
    }
    pendingEvents.delete(dedupeKey);
    return delivered;
  });
  pendingEvents.set(dedupeKey, delivery);
  return delivery;
};

// Campaign attribution captured at a learning boundary (e.g. exam start) so
// it can be persisted on the server session and survive the anonymous →
// registered handoff. Mirrors the metadata trackFunnelEvent sends.
export const getAttribution = () => {
  if (typeof window === "undefined") return {};
  return {
    ...captureAttribution(),
    anonymousId: getAnonymousId(),
  };
};
